import { useEffect, type RefObject } from 'react';

/**
 * Закрытие всплывающего: щелчок мимо и `Esc`.
 *
 * Одно правило на все выпадашки и окна кабинета. Меню, которое закрывается только
 * повторным щелчком по своей кнопке, остаётся висеть поверх экрана после перехода, и
 * его закрывают перезагрузкой.
 *
 * `ref` не задан — только `Esc`: у боковой панели и окна подтверждения щелчок мимо —
 * это щелчок по ленте или по затемнению, и случайно потерять введённое там нельзя.
 */
export function useDismiss(
  open: boolean,
  close: () => void,
  ref?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        close();
      }
    };
    const onPointer = (event: MouseEvent): void => {
      if (ref?.current != null && !ref.current.contains(event.target as Node)) {
        close();
      }
    };

    document.addEventListener('keydown', onKey);

    if (ref !== undefined) {
      document.addEventListener('mousedown', onPointer);
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open, close, ref]);
}
